import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Plus, Trash2, Pencil, Check, Star, Languages } from "lucide-react";

const inputCls = "mt-1 w-full rounded-lg border border-[#E5E5E0] px-3 py-2 text-sm bg-white";
const labelCls = "font-mono text-[10px] uppercase tracking-widest text-[#525252]";
const empty = { code: "", name: "", active: true, is_master: false, sort_order: 100 };

/** Iter 156 — Configuración → Idiomas: tabla de idiomas gestionados. */
export default function LanguagesManager({ languages, onChanged }) {
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const openNew = () => { setEditing(null); setForm({ ...empty, sort_order: (languages.length + 1) * 10 }); setOpen(true); };
  const openEdit = (l) => { setEditing(l); setForm({ code: l.code, name: l.name, active: l.active, is_master: l.is_master, sort_order: l.sort_order }); setOpen(true); };
  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/languages/${editing.id}`, form);
      else await api.post("/languages", form);
      toast.success("Idioma guardado"); setOpen(false); onChanged();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const toggle = async (l) => {
    try { await api.put(`/languages/${l.id}`, { active: !l.active }); onChanged(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const makeMaster = async (l) => {
    if (!window.confirm(`¿Convertir "${l.name}" en idioma maestro? El contenido base se escribirá en este idioma.`)) return;
    try { await api.put(`/languages/${l.id}`, { is_master: true }); onChanged(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (l) => {
    if (!window.confirm(`¿Eliminar el idioma ${l.name} (${l.code})?`)) return;
    try { await api.delete(`/languages/${l.id}`); toast.success("Idioma eliminado"); onChanged(); } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <section className="ds-card p-5" data-testid="languages-manager">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2"><Languages size={16} className="text-[#6E8B3D]" /><h3 className="font-display font-bold text-lg">Idiomas de comunicación</h3></div>
          <p className="text-xs text-[#525252] mt-1">Sólo existen los idiomas que configures aquí. El idioma maestro es el del contenido base (plantillas); el resto se traducen.</p>
        </div>
        <button onClick={openNew} className="ds-btn primary" data-testid="language-new-btn"><Plus size={14} /> Añadir idioma</button>
      </div>
      <div className="mt-4 rounded-xl border border-[#E5E5E0] overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className={`${labelCls} border-b border-[#E5E5E0] text-left`}>
            <th className="px-3 py-2">Orden</th><th className="px-3 py-2">Nombre</th><th className="px-3 py-2">Código</th><th className="px-3 py-2 text-center">Maestro</th><th className="px-3 py-2 text-center">Activo</th><th className="px-3 py-2 w-28"></th>
          </tr></thead>
          <tbody>
            {languages.map((l) => (
              <tr key={l.id} className="border-b border-[#E5E5E0] last:border-0" data-testid={`language-row-${l.code}`}>
                <td className="px-3 py-2 font-mono text-xs">{l.sort_order}</td>
                <td className="px-3 py-2 font-semibold">{l.name}</td>
                <td className="px-3 py-2 font-mono text-xs">{l.code}</td>
                <td className="px-3 py-2 text-center">
                  {l.is_master ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#F0F4E8] text-[#3F5222] font-mono text-[10px] uppercase tracking-widest" data-testid={`language-master-${l.code}`}><Star size={10} /> Maestro</span>
                    : <button onClick={() => makeMaster(l)} className="text-[10px] font-mono uppercase tracking-widest text-[#525252] hover:text-[#111110] underline" data-testid={`language-make-master-${l.code}`}>Hacer maestro</button>}
                </td>
                <td className="px-3 py-2 text-center">
                  <button onClick={() => toggle(l)} disabled={l.is_master} data-testid={`language-toggle-${l.code}`}
                    className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono uppercase tracking-widest disabled:opacity-60 ${l.active ? "bg-[#F0F4E8] text-[#3F5222]" : "bg-neutral-100 text-neutral-500"}`}>
                    {l.active ? <><Check size={10} /> Sí</> : "No"}
                  </button>
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => openEdit(l)} className="p-1.5 hover:bg-[#F0F0EB] rounded" data-testid={`language-edit-${l.code}`}><Pencil size={13} /></button>
                  {!l.is_master && <button onClick={() => remove(l)} className="p-1.5 hover:bg-red-50 text-red-700 rounded" data-testid={`language-delete-${l.code}`}><Trash2 size={13} /></button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onSubmit={save} className="bg-white rounded-2xl border border-[#E5E5E0] w-full max-w-md p-5 space-y-3" onClick={(e) => e.stopPropagation()} data-testid="language-dialog">
            <div className="font-display font-bold">{editing ? "Editar idioma" : "Nuevo idioma"}</div>
            <div><label className={labelCls}>Nombre</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="English (UK)" className={inputCls} data-testid="language-name-input" required /></div>
            <div><label className={labelCls}>Código de idioma</label><input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="en-GB" className={`${inputCls} font-mono`} data-testid="language-code-input" required /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Orden</label><input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value, 10) || 0 })} className={inputCls} /></div>
              <div className="flex flex-col justify-end gap-1 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-[#6E8B3D]" data-testid="language-active-input" /> Activo</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_master} onChange={(e) => setForm({ ...form, is_master: e.target.checked })} className="accent-[#6E8B3D]" /> Idioma maestro</label>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setOpen(false)} className="ds-btn">Cancelar</button>
              <button type="submit" className="ds-btn primary" data-testid="language-save-btn">Guardar</button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}
